<template>
  <section id="casos" class="section-padding bg-background-secondary">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-5xl font-bold mb-6">
          Experiencia <span class="text-gradient">Comprobada</span>
        </h2>
        <p class="text-xl text-muted-foreground max-w-3xl mx-auto">
          Conoce la trayectoria y experiencia de nuestros socios en diferentes industrias, 
          respaldando empresas mexicanas durante más de 20 años.
        </p>
      </div>

      <!-- Case Study Carousel -->
      <div class="relative">
        <div class="card-forza max-w-6xl mx-auto p-6">
          <div class="text-center pb-8">
            <div class="text-6xl mb-4">{{ currentCaseData.image }}</div>
            <h3 class="text-2xl md:text-3xl font-bold">
              {{ currentCaseData.client }}
            </h3>
            <p class="text-primary font-medium">{{ currentCaseData.industry }}</p>
          </div>

          <div class="space-y-8">
            <!-- Challenge & Solution -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 class="text-lg font-semibold mb-3 flex items-center">
                  <div class="w-2 h-2 bg-red-400 rounded-full mr-3"></div>
                  Desafío
                </h4>
                <p class="text-muted-foreground leading-relaxed">
                  {{ currentCaseData.challenge }}
                </p>
              </div>
              
              <div>
                <h4 class="text-lg font-semibold mb-3 flex items-center">
                  <div class="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                  Experiencia aplicada
                </h4>
                <p class="text-muted-foreground leading-relaxed">
                  {{ currentCaseData.solution }}
                </p>
              </div>
            </div>

            <!-- Results -->
            <div>
              <h4 class="text-lg font-semibold mb-6 flex items-center">
                <TrendingUp class="w-5 h-5 text-primary mr-3" />
                Resultados
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div 
                  v-for="(result, index) in currentCaseData.results"
                  :key="index"
                  class="text-center p-4 bg-primary/5 rounded-lg border border-primary/10"
                >
                  <div class="text-3xl font-bold text-primary mb-2">
                    {{ result.metric }}
                  </div>
                  <div class="text-sm text-muted-foreground">
                    {{ result.description }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Technologies -->
            <div>
              <h4 class="text-lg font-semibold mb-4">Tecnologías utilizadas</h4>
              <div class="flex flex-wrap gap-2">
                <span 
                  v-for="(tech, index) in currentCaseData.technologies"
                  :key="index"
                  class="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                >
                  {{ tech }}
                </span>
              </div>
            </div>

            <!-- Experience Note -->
            <div class="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-lg border border-primary/20">
              <div class="text-center">
                <h5 class="text-lg font-semibold mb-2">Experiencia en {{ currentCaseData.industry }}</h5>
                <p class="text-muted-foreground">
                  Nuestros socios han trabajado con empresas líderes en este sector, 
                  aportando soluciones tecnológicas probadas y adaptadas a las necesidades específicas.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="flex justify-center items-center mt-8 space-x-4">
          <button
            @click="prevCase"
            class="rounded-full p-2 border border-border hover:bg-accent transition-colors"
          >
            <ChevronLeft class="w-4 h-4" />
          </button>

          <div class="flex space-x-2">
            <button
              v-for="(_, index) in cases"
              :key="index"
              @click="currentCase = index"
              :class="[
                'w-3 h-3 rounded-full transition-colors duration-300',
                index === currentCase ? 'bg-primary' : 'bg-muted'
              ]"
            />
          </div>

          <button
            @click="nextCase"
            class="rounded-full p-2 border border-border hover:bg-accent transition-colors"
          >
            <ChevronRight class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- CTA -->
      <div class="text-center mt-16">
        <h3 class="text-2xl font-semibold mb-4">
          ¿Listo para aplicar nuestra experiencia en tu empresa?
        </h3>
        <p class="text-muted-foreground mb-6">
          Aprovecha más de 20 años de experiencia de nuestros socios para transformar tu negocio
        </p>
        <button
          @click="openCalendar"
          class="btn-primary cursor-pointer relative z-10"
          style="pointer-events: auto;"
        >
          Iniciar mi proyecto
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ChevronLeft, ChevronRight, TrendingUp } from 'lucide-vue-next'

const currentCase = ref(0)

const cases = [
  {
    client: "Retailer Especializado: Papelería, Tecnología y Muebles de Oficina.",
    industry: "Retail (papelería y tecnología)",
    founders: "Julián López, Iván Guzmán, Isaac Sánchez",
    challenge: "Transformar una organización tradicional hacia una operación digital, orientada a datos y con infraestructura moderna.",
    solution: "Rediseño completo de la estrategia tecnológica bajo un enfoque data-driven. Implementación de nuevos ERPs, herramientas de Business Intelligence y plataformas cloud (Google Workspace, Exchange, SharePoint). Fortalecimiento de la ciberseguridad (Cisco, Fortinet) y reducción de complejidad operativa mediante automatización y migración a entornos virtualizados (VMware, HiperV).",
    results: [
      { metric: "+20%", description: "Eficiencia en procesos internos (BI)" },
      { metric: "Consolidación", description: "Infraestructura unificada" },
      { metric: "Reducción", description: "Costos operativos" }
    ],
    testimonial: "La transformación digital que logramos con Forza TI nos permitió evolucionar de una operación tradicional a una organización completamente orientada a datos.",
    author: "Director de TI",
    technologies: ["Google Workspace", "Exchange", "SharePoint", "Business Intelligence", "VMware", "HiperV", "Cisco", "Fortinet"],
    image: "🏢"
  },
  {
    client: "energía, ingeniería y construcción",
    industry: "Servicios para energía, ingeniería y construcción",
    founders: "Julián López, Iván Guzmán, Hugo Hermida",
    challenge: "Estandarizar operaciones TI en distintas ubicaciones remotas con baja conectividad, alto riesgo en seguridad y bajo control de activos.",
    solution: "Implementación de SAP en múltiples sedes. Instalación de redes LAN/WAN/Wireless en zonas industriales y marítimas (plataformas). Soporte Basis de SAP, infraestructura de respaldo y políticas de ciberseguridad. Consolidación y control de activos mediante sistemas de administración centralizados.",
    results: [
      { metric: "30%", description: "Mejora en tiempos de aprovisionamiento TI" },
      { metric: "25%", description: "Reducción en incidencias críticas" },
      { metric: "Estandarización", description: "Operaciones en ubicaciones remotas" }
    ],
    testimonial: "Forza TI logró lo que parecía imposible: estandarizar nuestras operaciones tecnológicas en plataformas marítimas y zonas industriales remotas con conectividad limitada.",
    author: "Gerente de Sistemas",
    technologies: ["SAP", "Redes LAN/WAN/Wireless", "Soporte Basis", "Ciberseguridad", "Administración de Activos"],
    image: "🏗️"
  },
  {
    client: "Retailer Especializado: Librería",
    industry: "Retail editorial (venta de libros y cultura)",
    founders: "Julián López, Isaac Sánchez",
    challenge: "Optimizar el ecosistema tecnológico para soportar crecimiento online y mejorar la experiencia omnicanal del cliente.",
    solution: "Implementación de herramientas de Business Intelligence y rediseño de arquitectura de datos. Mejora del POS con nueva infraestructura de punto de venta. Creación de cubos OLAP para reporting operativo y estratégico. Soporte al crecimiento digital y mejora en procesos internos de logística.",
    results: [
      { metric: "+35%", description: "Velocidad de consulta de reportes" },
      { metric: "40%", description: "Reducción en tiempos de respuesta del POS" },
      { metric: "Omnicanal", description: "Experiencia unificada del cliente" }
    ],
    testimonial: "La implementación de Business Intelligence transformó completamente nuestra capacidad de tomar decisiones basadas en datos y optimizar la experiencia del cliente.",
    author: "Director de Tecnología",
    technologies: ["Business Intelligence", "Cubos OLAP", "POS", "Arquitectura de Datos", "Logística Digital"],
    image: "📚"
  },
  {
    client: "Retailer Especializado: Muebles y línea blanca",
    industry: "Retail (muebles y hogar)",
    founders: "Julián López, Iván Guzmán, Hugo Hermida, Isaac Sánchez",
    challenge: "Transformar la infraestructura TI y los sistemas centrales ante una operación nacional en expansión.",
    solution: "Implementación de SAP ERP en módulos clave (FICO, logística). Modernización de POS e integración con Intelisis. Arquitectura de redes LAN/WAN para más de 70 sucursales. Implementación de SharePoint, Exchange, políticas de seguridad, respaldos y telefonía IP.",
    results: [
      { metric: "50%", description: "Mejora en control de inventario" },
      { metric: "30%", description: "Reducción en incidencias críticas" },
      { metric: "70+", description: "Sucursales interconectadas" }
    ],
    testimonial: "La expansión nacional hubiera sido imposible sin la infraestructura tecnológica robusta que implementó Forza TI. SAP nos dio el control total que necesitábamos.",
    author: "Gerente de Operaciones",
    technologies: ["SAP ERP", "FICO", "Intelisis", "POS", "Redes LAN/WAN", "SharePoint", "Exchange", "Telefonía IP"],
    image: "🪑"
  }
]

const currentCaseData = computed(() => cases[currentCase.value])

const nextCase = () => {
  currentCase.value = (currentCase.value + 1) % cases.length
}

const prevCase = () => {
  currentCase.value = (currentCase.value - 1 + cases.length) % cases.length
}

const openCalendar = () => {
  window.open('https://calendar.app.google/sm14g81D1o1j4peJ6', '_blank')
}
</script>